<template>
  <div class="view-content creator-view">
    <h1 class="sr-only">Design dine skydelåger</h1>
    <div
      v-if="loadError"
      class="alert alert-danger mx-auto my-auto px-5">
      Fejl ved indlæsning af data.
      <a
        href="javascript:window.location.reload();"
        class="alert-link">
        Opdatere siden
      </a>
      for at prøve igen.
    </div>
    <template v-else>
      <div
        v-if="!allDataLoaded || isStepChangeInProgress"
        class="loading-overlay">
        <div class="loader">
          <span>Indlæsning af data&hellip;</span>
        </div>
      </div>

      <wardrobe-preview>
        <template v-slot:content>
          <div
            v-if="selectedDoorsNumberOption"
            class="wardrobe-layout"
            :data-doors-count="selectedDoorsNumberOption.number">
            <div
              :class="{
                'wardrobe-front': true,
                'has-no-doors': !selectedDoorsNumberOption || !selectedDoorsNumberOption.number
              }"
              v-bind:style="{ 'aspect-ratio': (wardrobeWidth / wardrobeHeight) }">
              <div
                v-for="(door, index) in selectedDoorsNumberOption.number"
                :key="index"
                class="wardrobe-door">
                <span class="wardrobe-door-text">Dør bredde: {{ selectedDoorsNumberOption.door_width || 0 }}&nbsp;mm</span>
              </div>
            </div>
          </div>
        </template>
      </wardrobe-preview>

      <right-sidebar :view-local-state="localState">
        <template v-slot:header>
          <h2>Skydelåger design</h2>
        </template>

        <template v-slot:content>
          <validation-observer
            v-if="allDataLoaded"
            ref="doors-number-form"
            tag="form"
            id="doorsNumberForm"
            class="doors-number-form-modificate">

            <validation-provider
              rules="required"
              name="number-of-doors"
              v-slot="{ errors }"
              class="door-no-selection grid-cols-1 mb-2"
              tag="div">

              <c-form-radio
                v-for="(countOption, index) in doorsCountOptions"
                :key="index"
                :withImage="true"
                :value="countOption.number"
                :checked="countOption.number === data.doorsNumber"
                class="mx-auto"
                v-model="data.doorsNumber"
                :class="{ 'is-invalid': errors.length }" >
                <span>{{ countOption.name }}</span>
                <img
                  class="door-count-img"
                  :src="require('@/assets/images/door-count/' + countOption.number + '-doors.png')"
                  :alt="'Antal Skydedøre - ' + countOption.number" />
              </c-form-radio>
              <div
                v-if="errors.length"
                class="invalid-feedback text-center mt-0">
                Vælg venligst antal døre.
              </div>
            </validation-provider>

            <div class="form-group flex-justify-content-center">
              <validation-provider
                name="door-sofclose"
                v-slot="{ errors }">
                <div>
                  <c-checkbox
                    v-model="data.hasSoftClose">
                    Tilføj softclose på alle låger
                  </c-checkbox>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </validation-observer>
        </template>

        <template v-slot:footer>
          <navigation-buttons
            :form-id="'doorsNumberForm'"
            @nextBtnClicked="validateForm"
            @backBtnClicked="goBack" />
        </template>
      </right-sidebar>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';
import StateUtils from '@/utils/StateUtils.js'

export default {
  name: 'wardrobe-doors-numer',
  computed: {
    ...mapGetters('creator', [
      'wardrobeHeight',
      'wardrobeWidth',
      'wardrobeGableId',
      'wardrobeImpact',
      'wardrobePositionId',
      'doors',
      'hasDoor',
      'hasInventory'
    ]),
    allDataLoaded () {
      return this.loaded && this.doorsCountOptionsLoaded;
    },
    selectedDoorsNumberOption () {
      if (!this.doorsCountOptionsLoaded || this.loadError) {
        return null;
      }

      return this.doorsCountOptions.find(item => item.number === this.data.doorsNumber);
    },
    localState () {
      let output = StateUtils.getDoorsStateForNumber(this.doors, this.data.doorsNumber, this.data.hasSoftClose);

      for (let i = 0; i < output.items.length; i++) {
        output.items[i].soft_close = this.data.hasSoftClose;
      }

      return { doors: output, doorsNumberObject: this.selectedDoorsNumberOption };
    }
  },
  data () {
    return {
      data: {
        doorsNumber: null,
        hasSoftClose: false
      },
      doorsCountOptions: [],
      doorsCountOptionsLoaded: false,
      isStepChangeInProgress: false,
      loaded: false,
      loadError: false
    }
  },
  mounted () {
    this.loadOptionsData();

    this.data.hasSoftClose = this.$store.getters['creator/wardrobeSoftClose'];

    this.loaded = true;
  },
  methods: {
    loadOptionsData () {
      FormUtils.loadData(this, {
        method: 'post',
        endpoint: '/api/doors/calculate/' + this.wardrobeWidth,
        loadedKey: 'doorsCountOptionsLoaded',
        outputKey: 'doorsCountOptions',
        errorKey: 'loadError',
        noPagination: true,
        params: {
          has_gable: !!this.wardrobeGableId,
          has_impact: this.wardrobeImpact,
          wardrobe_position: this.wardrobePositionId
        },
        successAction: (response) => {
          let doorsNumberInStore = this.$store.getters['creator/doorsNumber'];

          if (doorsNumberInStore) {
            let currentOption = response.find(item => item.number === doorsNumberInStore)
            if (currentOption) {
              this.data.doorsNumber = doorsNumberInStore;
            } else {
              this.data.doorsNumber = response[0].number;
            }
          } else {
            this.data.doorsNumber = response[0].number;
          }
        }
      });
    },
    validateForm () {
      Vue.set(this, 'isStepChangeInProgress', true);
      FormUtils.validate(this.$refs['doors-number-form'], this.confirmSubmitForm, this.handleStepChangeStop);
    },
    handleStepChangeStop () {
      Vue.set(this, 'isStepChangeInProgress', false);
    },
    async confirmSubmitForm () {
      let isChanged = this.areChangesMade();
      if (this.$store.getters['creator/getIsStepCompleted']('wardrobeDoorsNumber') && isChanged) {
        Vue.swal({
          title: 'Note!',
          text: 'Ændringer i dette trin, kan have indflydelse på senere trin.',
          icon: 'warning',
          confirmButtonText: 'OK',
        }).then(async () => {
          await this.saveState(true, isChanged);

          this.handleStepChangeStop();
          this.$router.push({ name: 'wardrobeDoorsDivision' });
        });
      } else {
        await this.saveState(true);

        this.handleStepChangeStop();
        this.$router.push({ name: 'wardrobeDoorsDivision' });
      }
    },
    async saveState (shouldMarkStepCompleted = false, isSettingChanged = false) {
      if (isSettingChanged) {
        this.$store.commit('creator/resetStepsAfter', 'wardrobeDoorsNumber');
      }

      this.$store.commit('creator/setDoorsNumber', this.data.doorsNumber);
      this.$store.commit('creator/setWardrobeSoftClose', this.data.hasSoftClose);

      this.$store.commit('creator/setDoorsNumberObject', this.selectedDoorsNumberOption);

      if (shouldMarkStepCompleted) {
        this.$store.commit('creator/setStepCompletion', { stepName: 'wardrobeDoorsNumber', stepCompleted: true });
      }

      await this.$nextTick();
    },
    async goBack () {
      Vue.set(this, 'isStepChangeInProgress', true);
      await this.saveState();

      this.handleStepChangeStop();
      if (this.hasDoor && !this.hasInventory) {
        this.$router.push({ name: 'wardrobeDimensions' });
        return;
      }

      this.$router.push({ name: 'wardrobePosition' });
    },
    areChangesMade () {
      return StateUtils.isDoorsNumberChanged(this.localState, this.$store.getters['creator/creatorWardrobeData']);
    }
  }
};
</script>
